/* Presentation only: Moon Bundles remains responsible for variants and cart prices. */
(() => {
  if (customElements.get('matelas-black-friday')) return;

  const parsePrice = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
    const price = Number(normalized);
    return Number.isFinite(price) ? price : null;
  };

  // Convert a wall-clock date to an instant in the configured market timezone.
  // Iteration accounts for the UTC offset changing before the next Monday (DST).
  const zonedParts = (date, formatter) => Object.fromEntries(
    formatter.formatToParts(date).filter(({ type }) => type !== 'literal').map(({ type, value }) => [type, Number(value)])
  );
  const wallTime = (parts) => Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  const nextMonday = (now, formatter) => {
    const parts = zonedParts(now, formatter);
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    const days = (8 - date.getUTCDay()) % 7 || 7;
    const target = date.getTime() + days * 86400000;
    let instant = target;
    for (let i = 0; i < 4; i += 1) {
      const correction = target - wallTime(zonedParts(new Date(instant), formatter));
      instant += correction;
      if (!correction) break;
    }
    return instant;
  };

  class MatelasBlackFriday extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.root = this.closest('[data-matelas-black-friday]');
      const config = this.querySelector('[data-bf-config]');
      if (!this.root || !config) return;
      this.initialized = true;
      this.config = JSON.parse(config.textContent);
      this.money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: window.Shopify?.currency?.active || this.config.currency });
      const dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' };
      try {
        this.dateFormatter = new Intl.DateTimeFormat('en-GB', { ...dateOptions, timeZone: this.config.timezone });
      } catch (_) {
        this.dateFormatter = new Intl.DateTimeFormat('en-GB', { ...dateOptions, timeZone: 'Europe/Paris' });
      }
      this.onInteraction = this.handleInteraction.bind(this);
      this.root.addEventListener('click', this.onInteraction, true);
      this.root.addEventListener('change', this.onInteraction, true);
      this.root.addEventListener('keydown', this.onInteraction, true);
      this.observer = new MutationObserver((mutations) => {
        // Ignore our own additions and timer updates. Moon Bundles can mount or rerender asynchronously.
        if (mutations.some(({ target }) => !target.parentElement?.closest('[data-bf-owned], matelas-black-friday, [data-bf-countdown]') && !target.closest?.('[data-bf-owned], matelas-black-friday, [data-bf-countdown]'))) {
          this.scheduleRefresh();
        }
      });
      this.observer.observe(this.root, { childList: true, subtree: true, characterData: true });
      this.refresh();
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearInterval(this.interval);
      cancelAnimationFrame(this.frame);
      ['click', 'change', 'keydown'].forEach((type) => this.root?.removeEventListener(type, this.onInteraction, true));
      this.initialized = false;
    }

    scheduleRefresh() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.refresh();
      });
    }

    handleInteraction(event) {
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      const overlay = event.target.closest('[data-bf-landing]:not(.matelas-bf-landing--thumbnail)');
      if (overlay && this.root.dataset.bfLandingDismissed !== 'true') {
        // The promotion image is not the product image registered with PhotoSwipe.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const variant = event.target.closest('moonbundle-component .moonbundle-swatch-option-value, moonbundle-component select, moonbundle-component .moonbundle-dropdown-option');
      if (!variant || variant.closest('.moonbundle-swatch-oos, [aria-disabled="true"]')) return;
      this.root.dataset.bfLandingDismissed = 'true';
      if (variant.dataset.value) {
        this.root.dispatchEvent(new CustomEvent('coziya:bundle-variant', {
          detail: { sectionId: this.config.sectionId, color: variant.dataset.value }
        }));
      }
    }

    insertText(parent, className, text) {
      let element = parent.querySelector(`:scope > .${className}`);
      if (!element) {
        element = document.createElement('span');
        element.className = className;
        element.dataset.bfOwned = '';
        parent.append(element);
      }
      if (element.textContent !== text) element.textContent = text;
      return element;
    }

    readBundleTotal(option, quantity) {
      // The existing Moon Bundles custom script rewrites the main DUO/TRIO price
      // to fixed EUR unit prices. The subtitle retains the app's current pack total.
      const subtitle = option.querySelector('.moonbundle-option-subtitle');
      const main = option.querySelector('.moonbundle-option-single-price');
      let source = quantity > 1 ? subtitle?.textContent : main?.textContent;
      if (!source?.trim() && !main?.querySelector('.lec-unit-suffix, [data-lec-applied]')) {
        source = main?.textContent;
      }
      const amount = source?.match(/\d[\d\s\u00a0\u202f.,'’]*/)?.[0].trim();
      if (!amount) return null;
      let normalized = amount.replace(/[\s\u00a0\u202f'’]/g, '');
      const comma = normalized.lastIndexOf(',');
      const dot = normalized.lastIndexOf('.');
      const separator = Math.max(comma, dot);
      if (separator >= 0) {
        const decimals = normalized.length - separator - 1;
        const currencyDecimals = this.money.resolvedOptions().maximumFractionDigits;
        const isDecimal = comma >= 0 && dot >= 0 || decimals <= 2 || currencyDecimals === 3 && decimals === 3;
        normalized = isDecimal
          ? normalized.slice(0, separator).replace(/[.,]/g, '') + '.' + normalized.slice(separator + 1)
          : normalized.replace(/[.,]/g, '');
      }
      const value = Number(normalized);
      return Number.isFinite(value) ? value : null;
    }

    compactDiscount(value) {
      const label = String(value ?? '').trim();
      // Keep the market's amount/currency (or percentage), only shorten its prefix.
      return label.replace(/^save\s+(?=.*\d)/i, '−');
    }

    refresh() {
      const bundle = this.root.querySelector('.product-content moonbundle-component');
      if (!bundle) return;
      const bannerTemplate = this.querySelector('[data-bf-banner-template]');
      if (bannerTemplate && !this.root.querySelector('[data-bf-banner]')) {
        const banner = bannerTemplate.content.firstElementChild.cloneNode(true);
        banner.dataset.bfOwned = '';
        bundle.before(banner);
      }
      const badgeTemplate = this.querySelector('[data-bf-badge-template]');
      bundle.querySelectorAll('.moonbundle-option-wrapper').forEach((wrapper, index) => {
        const quantity = wrapper.querySelectorAll('.moonbundle-variant-row').length;
        const offer = this.config.offers.find((item) => item.quantity === quantity) || this.config.offers[index];
        if (!offer) return;
        const option = wrapper.querySelector('.moonbundle-option');
        if (!option) return;
        const row = option.querySelector('.moonbundle-option-col-inner');
        const pills = option.querySelector('.moonbundle-option-pills') || row;
        if (badgeTemplate && pills && !pills.querySelector('[data-bf-gift-badge]')) {
          const badge = badgeTemplate.content.firstElementChild.cloneNode(true);
          badge.dataset.bfOwned = '';
          pills.append(badge);
        }
        const nativeDiscount = option.querySelector('.moonbundle-label-inside')?.textContent;
        const discount = this.compactDiscount(String(offer.discount ?? '').trim() || nativeDiscount);
        if (discount && pills) {
          option.dataset.bfDiscountOverride = '';
          const badge = this.insertText(pills, 'matelas-bf-discount', discount);
          if (pills.firstElementChild !== badge) pills.prepend(badge);
        } else {
          delete option.dataset.bfDiscountOverride;
          option.querySelector('.matelas-bf-discount')?.remove();
        }
        const price = parsePrice(offer.price) ?? this.readBundleTotal(option, offer.quantity);
        const priceContainer = option.querySelector('.moonbundle-option-price');
        const column = option.querySelector('.moonbundle-option-col');
        if (price !== null && priceContainer) {
          option.dataset.bfPriceOverride = '';
          let unitPrice = priceContainer.querySelector(':scope > .matelas-bf-price');
          if (!unitPrice) {
            unitPrice = this.insertText(priceContainer, 'matelas-bf-price', '');
            unitPrice.append(document.createElement('span'));
          }
          const amount = unitPrice.firstElementChild;
          const formatted = this.money.format(price / offer.quantity);
          if (amount.textContent !== formatted) amount.textContent = formatted;
          if (offer.quantity > 1) {
            // Both values follow the explicit override, or the live Moon Bundles total.
            if (!unitPrice.querySelector('small')) {
              const suffix = document.createElement('small');
              suffix.textContent = this.config.unit;
              unitPrice.append(suffix);
            }
            if (column) this.insertText(column, 'matelas-bf-total', this.money.format(price));
          }
        } else {
          delete option.dataset.bfPriceOverride;
          option.querySelector('.matelas-bf-price')?.remove();
          option.querySelector('.matelas-bf-total')?.remove();
        }
      });
    }

    tick(now = new Date()) {
      if (!this.deadline || now.getTime() >= this.deadline || now.getTime() < this.lastTick) {
        this.deadline = nextMonday(now, this.dateFormatter);
      }
      this.lastTick = now.getTime();
      const seconds = Math.max(0, Math.floor((this.deadline - now.getTime()) / 1000));
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor(seconds % 86400 / 3600);
      const minutes = Math.floor(seconds % 3600 / 60);
      const value = `${days}${this.config.day} ${hours}${this.config.hour} ${minutes}${this.config.minute}`;
      this.root.querySelectorAll('[data-bf-countdown]').forEach((countdown) => {
        const time = countdown.querySelector('[data-bf-time]');
        if (time.textContent !== value) time.textContent = value;
        countdown.hidden = false;
      });
    }
  }

  customElements.define('matelas-black-friday', MatelasBlackFriday);
})();
